// StagingFileInput — stages files for a deferred upload without sending
// them. Used on create forms (e.g. Skip enquiry create) where the parent
// record's enc_id does not exist yet, so files must be queued and uploaded
// once the record has been created.
//
// Accepts JPEG, PNG, WebP and PDF. Returns the staged File[] via onChange.
// No compression and no network calls — the caller uploads after create
// and the server validates size/mime by magic bytes.

import { useCallback, useRef } from 'react';
import { Paperclip, X } from 'lucide-react';
import styles from './StagingFileInput.module.css';

const ACCEPT_MIMES = 'image/jpeg,image/png,image/webp,application/pdf';
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

export interface StagingFileInputProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** Upper bound on staged files. Mirrors MAX_ATTACHMENTS_PER_ENQUIRY. */
  maxFiles?: number;
  disabled?: boolean;
}

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function StagingFileInput({
  files,
  onChange,
  maxFiles = 20,
  disabled = false,
}: StagingFileInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || disabled) return;
      const remaining = maxFiles - files.length;
      const incoming = Array.from(fileList)
        // Hint-level mime filter; server is authoritative (magic bytes).
        .filter((f) => ACCEPTED.includes(f.type))
        .slice(0, Math.max(0, remaining));
      if (incoming.length) onChange([...files, ...incoming]);
      // Reset so re-selecting the same file fires onChange again.
      if (inputRef.current) inputRef.current.value = '';
    },
    [disabled, files, maxFiles, onChange],
  );

  function remove(idx: number): void {
    onChange(files.filter((_, i) => i !== idx));
  }

  const canAdd = files.length < maxFiles && !disabled;

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => inputRef.current?.click()}
          disabled={!canAdd}
        >
          <Paperclip size={16} aria-hidden />
          <span>Add files</span>
        </button>
        <span className={styles.hint}>
          JPEG, PNG, WebP or PDF — up to {maxFiles}. Files will upload after the
          enquiry is saved.
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_MIMES}
        className={styles.hiddenInput}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />

      {files.length > 0 ? (
        <ul className={styles.list}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className={styles.item}>
              <span className={styles.itemMeta}>
                <span className={styles.filename}>{f.name}</span>
                <span className={styles.filesize}>{fmtBytes(f.size)}</span>
              </span>
              {!disabled ? (
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => remove(i)}
                  aria-label={`Remove ${f.name}`}
                >
                  <X size={14} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
